import {useQueryClient} from '@tanstack/react-query'
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import {businessTimestampFromDate} from 'src/api/business-time'

import {invalidateResourceAssignmentQueries} from '../assignments/invalidate-resource-assignment-queries'
import {
    monthRangeFromOffset,
    weekRangeFromWeekOffset,
} from '../dispatch-timeline/time-navigation-helpers'
import type {ViewRange} from '../dispatch-timeline/types'
import type {DispatchTimelineData, JobAllocation, RequirementKind} from '../dispatch-timeline/types'
import {buildWorkDaySlots} from '../dispatch-timeline/work-day'
import {invalidateEquipmentMoves} from '../project-deployed-equipment/invalidate-equipment-moves'
import {
    createTestDispatchDataLayer,
    type DispatchDataLayer,
    dispatchDataLayer,
    type StockDeltaInput,
    type StockReassignmentInput,
} from './dispatch-data-layer'
import {buildTodayScheduleDate} from './dispatch-mapper'
import {dispatchKeys} from './query-keys'
import {useSyncDispatchAttentionCountFromBoard} from './use-dispatch'
import {invalidateAvailabilityBoards} from './use-job-availability-board'

function createEmptyBoard(): DispatchTimelineData {
    return {
        fieldLabel: 'FIELD OPERATIONS',
        scheduleDate: buildTodayScheduleDate(),
        timeSlots: buildWorkDaySlots(businessTimestampFromDate(new Date()).getHours()),
        resources: [],
        equipment: [],
        unassignedJobs: [],
        assignments: [],
        hourlyLoad: [],
    }
}

function boardWindowForView(viewRange: ViewRange, anchorOffset: number): [Date, Date] {
    return viewRange === 'month'
        ? monthRangeFromOffset(anchorOffset)
        : weekRangeFromWeekOffset(anchorOffset)
}

type AllocateResourceInput = {
    requirementId: string
    resourceId: string
    resourceName: string
    kind: RequirementKind
}

type DispatchBoardContextValue = {
    canonicalData: DispatchTimelineData
    isLoading: boolean
    error: string | null
    refresh: () => Promise<void>
    allocateResource: (jobId: string, input: AllocateResourceInput) => Promise<DispatchTimelineData>
    allocateResources: (
        jobId: string,
        inputs: AllocateResourceInput[],
    ) => Promise<DispatchTimelineData>
    replaceAllocations: (
        jobId: string,
        allocations: JobAllocation[],
    ) => Promise<DispatchTimelineData>
    applyStockDelta: (jobId: string, input: StockDeltaInput) => Promise<DispatchTimelineData>
    applyStockReassignment: (
        jobId: string,
        input: StockReassignmentInput,
        sourceJobIds: string[],
    ) => Promise<DispatchTimelineData>
    notifyDriver: (jobId: string, resourceId: string) => Promise<DispatchTimelineData>
}

const DispatchBoardContext = createContext<DispatchBoardContextValue | null>(null)

type DispatchBoardProviderProps = {
    children: ReactNode
    dataLayer?: DispatchDataLayer
    viewRange?: ViewRange
    anchorOffset?: number
}

export function DispatchBoardProvider({
    children,
    dataLayer = dispatchDataLayer,
    viewRange = 'week',
    anchorOffset = 0,
}: DispatchBoardProviderProps) {
    const queryClient = useQueryClient()
    const [canonicalData, setBoard] = useState<DispatchTimelineData>(createEmptyBoard)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const syncAttentionCount = useSyncDispatchAttentionCountFromBoard()
    const [startDate, endDate] = useMemo(
        () => boardWindowForView(viewRange, anchorOffset),
        [viewRange, anchorOffset],
    )
    // Latest committed board, read by queued saves.
    const boardRef = useRef(canonicalData)
    const latestLoadRef = useRef(0)
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve())

    const setCanonicalData = useCallback((next: DispatchTimelineData) => {
        boardRef.current = next
        setBoard(next)
    }, [])

    const invalidateAfterBoardChange = useCallback(() => {
        invalidateResourceAssignmentQueries(queryClient)
        void queryClient.invalidateQueries({queryKey: dispatchKeys.drivers()})
        invalidateAvailabilityBoards(queryClient)
        void invalidateEquipmentMoves(queryClient, null)
    }, [queryClient])

    const afterBoardMutation = useCallback(
        (next: DispatchTimelineData) => {
            setCanonicalData(next)
            syncAttentionCount()
            invalidateAfterBoardChange()
        },
        [invalidateAfterBoardChange, setCanonicalData, syncAttentionCount],
    )

    const refresh = useCallback(async () => {
        const loadId = latestLoadRef.current + 1
        latestLoadRef.current = loadId
        setIsLoading(true)
        setError(null)

        try {
            const board = await dataLayer.loadBoard(startDate, endDate)
            if (loadId !== latestLoadRef.current) {
                return
            }
            setCanonicalData(board)
            syncAttentionCount()
        } catch (loadError) {
            if (loadId !== latestLoadRef.current) {
                return
            }
            setError(
                loadError instanceof Error ? loadError.message : 'Failed to load dispatch board',
            )
        } finally {
            if (loadId === latestLoadRef.current) {
                setIsLoading(false)
            }
        }
    }, [dataLayer, endDate, setCanonicalData, startDate, syncAttentionCount])

    useEffect(() => {
        void refresh()
    }, [refresh])

    // Reload the board after a failed save, then rethrow the save error.
    const reloadAfterFailedSave = useCallback(
        async (saveError: unknown): Promise<never> => {
            const loadId = latestLoadRef.current + 1
            latestLoadRef.current = loadId
            try {
                const board = await dataLayer.loadBoard(startDate, endDate)
                if (loadId === latestLoadRef.current) {
                    setCanonicalData(board)
                    syncAttentionCount()
                }
                invalidateAfterBoardChange()
            } catch {
                // Reload errors are ignored.
            }
            throw saveError
        },
        [
            dataLayer,
            endDate,
            invalidateAfterBoardChange,
            setCanonicalData,
            startDate,
            syncAttentionCount,
        ],
    )

    // Serialize saves through a promise queue.
    const runSave = useCallback(
        (save: (board: DispatchTimelineData) => Promise<DispatchTimelineData>) => {
            const run = saveQueueRef.current.then(async () => {
                let next: DispatchTimelineData
                try {
                    next = await save(boardRef.current)
                } catch (saveError) {
                    return reloadAfterFailedSave(saveError)
                }
                afterBoardMutation(next)
                return next
            })
            saveQueueRef.current = run.catch(() => undefined)
            return run
        },
        [afterBoardMutation, reloadAfterFailedSave],
    )

    const allocateResources = useCallback(
        (jobId: string, inputs: AllocateResourceInput[]) =>
            runSave((board) => dataLayer.allocateResources(board, jobId, inputs)),
        [dataLayer, runSave],
    )

    const allocateResource = useCallback(
        async (jobId: string, input: AllocateResourceInput) => {
            return allocateResources(jobId, [input])
        },
        [allocateResources],
    )

    const replaceAllocations = useCallback(
        (jobId: string, allocations: JobAllocation[]) =>
            runSave((board) => dataLayer.replaceAllocations(board, jobId, allocations)),
        [dataLayer, runSave],
    )

    const applyStockDelta = useCallback(
        (jobId: string, input: StockDeltaInput) =>
            runSave((board) => dataLayer.applyStockDelta(board, jobId, input)),
        [dataLayer, runSave],
    )

    const applyStockReassignment = useCallback(
        (jobId: string, input: StockReassignmentInput, sourceJobIds: string[]) =>
            runSave((board) => dataLayer.applyStockReassignment(board, jobId, input, sourceJobIds)),
        [dataLayer, runSave],
    )

    const notifyDriverForJob = useCallback(
        async (jobId: string, resourceId: string) => {
            const next = await dataLayer.notifyDriver(boardRef.current, jobId, resourceId)
            setCanonicalData(next)
            syncAttentionCount()
            return next
        },
        [dataLayer, setCanonicalData, syncAttentionCount],
    )

    const value = useMemo(
        () => ({
            canonicalData,
            isLoading,
            error,
            refresh,
            allocateResource,
            allocateResources,
            replaceAllocations,
            applyStockDelta,
            applyStockReassignment,
            notifyDriver: notifyDriverForJob,
        }),
        [
            canonicalData,
            isLoading,
            error,
            refresh,
            allocateResource,
            allocateResources,
            replaceAllocations,
            applyStockDelta,
            applyStockReassignment,
            notifyDriverForJob,
        ],
    )

    return <DispatchBoardContext.Provider value={value}>{children}</DispatchBoardContext.Provider>
}

export function useDispatchBoardContext(): DispatchBoardContextValue {
    const value = useContext(DispatchBoardContext)
    if (value == null) {
        throw new Error('useDispatchBoardContext must be used within DispatchBoardProvider')
    }
    return value
}

export {createEmptyBoard, createTestDispatchDataLayer}
